import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../u2000Core/axios/U2000UseAxiosAuthenticated";
import { Outlet } from "react-router-dom";
import i18next from "i18next";
import { useU2000InitLUHandlers } from "../u2000Core/hooks/handler/U2000UseInitLUHandlers";
import { U2000HandleApiError } from "../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../u2000Core/context/toast/U2000ToastContext";
import { doInitLU, luHandleApiError, type U3130InitLU } from "./U3130Service";
import { U3130ContextProvider } from "./U3130Context";
import { useU2000LogicalUnitContext } from "../u2000Core/context/logicalUnit/U2000LogicalUnitContext";
import { U3130_NS } from "./U3130I18n";

export const NS = U3130_NS;

export const U3130ROLES = {
  READ: "U3130:READ",
  EDIT: "U3130:EDIT",
  AUDITS: "U3130:AUDITS",
} as const;
export type U3130ROLES = (typeof U3130ROLES)[keyof typeof U3130ROLES];

const defaultInitU3130: U3130InitLU = {
  launchSearch: false,
  /** Barre compacte par défaut (comme U3129) : panneau Critères / Tri / Colonnes replié. */
  hideSearch: true,
  nbRecordPerPage: 50,
  equipmentTypes: [],
  taxRates: [],
  expenseTypes: [],
  personalization: { admIncludeSubProject: null, skipHourBankBalanceValidationOnSubmit: false, hideExpenseAndEquipmentTabs: false },
  employee: null,
};

const U3130: React.FC = () => {
  const { i18n } = useTranslation();
  const [i18nReady, setI18nReady] = useState<boolean>(false);
  const { t } = useTranslation();
  const toast = useToast();
  const [initLUReady, setInitLUReady] = useState<boolean>(false);
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const [initLUData, setInitLUData] = useState<U3130InitLU>(defaultInitU3130);

  useEffect(() => {
    i18next.loadNamespaces([NS], (err) => {
      if (err) return console.error("Error loading namespace:", err);
      setI18nReady(true);
    });
  }, [i18n]);

  const handleInitLU = useU2000InitLUHandlers<U3130InitLU>({
    doInitLUFn: async () => await doInitLU(u2000AxiosAuthenticated),
    onSuccess: (payload: U3130InitLU) => {
      // Comme U3129 : ne pas conserver tasks en mémoire (tâches honoraires = findTasksForProject par ligne).
      const rest = { ...payload };
      delete rest.tasks;
      setInitLUData({ ...defaultInitU3130, ...rest });
      setInitLUReady(true);
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
      setInitLUData(defaultInitU3130);
      setInitLUReady(true);
    },
  });

  useEffect(() => {
    handleInitLU.getInitLU();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- init LU au montage (handlers stables)
  }, [t]);

  const { setLogicalUnitTitle, setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  useEffect(() => {
    if (!i18nReady || !initLUReady) return;
    const title = t("title", { ns: NS });
    setLogicalUnitTitle(title);
    return () => {
      setLogicalUnitTitle(null);
      setLogicalUnitBreadcrumb([]);
    };
  }, [i18nReady, initLUReady, t, setLogicalUnitTitle, setLogicalUnitBreadcrumb]);

  if (!i18nReady || !initLUReady) return null;

  const initForContext: U3130InitLU = {
    ...defaultInitU3130,
    ...initLUData,
    equipmentTypes: initLUData.equipmentTypes ?? [],
    taxRates: initLUData.taxRates ?? [],
    expenseTypes: initLUData.expenseTypes ?? [],
    personalization: initLUData.personalization ?? { admIncludeSubProject: null },
    activePayrollPeriod: initLUData.activePayrollPeriod,
    sommairePaiePayrollPeriods: initLUData.sommairePaiePayrollPeriods,
    /** L’init LU API peut renvoyer hideSearch: false ; on force la barre compacte (comportement U3129). */
    hideSearch: true,
  };

  return (
    <U3130ContextProvider initLU={initForContext}>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <Outlet />
      </div>
    </U3130ContextProvider>
  );
};

export default U3130;
